import { useMutation, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import {
  OpalApiComponent,
  OpalApiResponseDocument,
  customFields,
} from '@opallabs/api/v3'
import { OpalApiRequestBody } from '../../../schema'
import { globalQueryKeys } from '../../queryKeys'
import { customFieldsCollectionParams } from './selectors'
import { type FlattenedCustomField } from './types'

type RequestBody = OpalApiRequestBody<'CreateCustomFieldV3'>
type RequestBodyParams = Readonly<
  Pick<FlattenedCustomField, 'name'> &
    Partial<
      Pick<
        FlattenedCustomField,
        | 'defaultNotificationMode'
        | 'description'
        | 'isCascading'
        | 'isColorDisabled'
        | 'metadata'
        | 'pairedLabelSetId'
      >
    > & {
      fieldTypeId: string
      workspaceId: string
    }
>

function buildRequestBody({
  name,
  fieldTypeId,
  workspaceId,
  metadata = {},
  description,
  isColorDisabled = true,
  isCascading = false,
  defaultNotificationMode = 'manual',
  pairedLabelSetId,
}: RequestBodyParams): RequestBody {
  const optionalRelationships: Partial<RequestBody['data']['relationships']> =
    {}
  if (pairedLabelSetId) {
    optionalRelationships.paired_label_set = {
      data: {
        id: pairedLabelSetId,
        type: 'label_set',
      },
    }
  }

  return {
    data: {
      type: 'custom_field',
      attributes: {
        display_name: name,
        metadata,
        description,
        is_color_disabled: isColorDisabled,
        is_cascading: isCascading,
        default_notification_mode: defaultNotificationMode,
      },
      relationships: {
        ...optionalRelationships,
        custom_field_type: {
          data: {
            id: fieldTypeId,
            type: 'custom_field_type',
          },
        },
        workspace: {
          data: {
            id: workspaceId,
            type: 'workspace',
          },
        },
      },
    },
  }
}

export function useCreateCustomField() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: RequestBodyParams) =>
      customFields.create({
        requestBody: buildRequestBody(params),
      }),
    onMutate({
      workspaceId,
      name,
      fieldTypeId,
      metadata = {},
      description,
      isColorDisabled,
      isCascading,
      defaultNotificationMode = 'manual',
    }) {
      const newCustomField = customFields.clientResource({
        attributes: {
          display_name: name,
          description,
          is_color_disabled: isColorDisabled,
          metadata,
          is_cascading: isCascading,
          default_notification_mode: defaultNotificationMode,
        },
        relationships: {
          custom_field_type: {
            data: {
              id: fieldTypeId,
              type: 'custom_field_type',
            },
          },
          workspace: {
            data: {
              id: workspaceId,
              type: 'workspace',
            },
          },
          active_options: { data: [] },
          deleted_options: { data: [] },
          creator: {
            data: {
              type: 'user',
              id: '',
            },
          },
        },
      })

      queryClient.setQueryData<
        OpalApiResponseDocument<'ReadCustomFieldsV3', 200>
      >(
        globalQueryKeys.customFields(
          workspaceId,
          customFieldsCollectionParams(workspaceId)
        ),
        (customFieldsDocument) => {
          const fieldTypes = queryClient.getQueryData<
            OpalApiResponseDocument<'ReadCustomFieldTypesV3', 200>
          >(globalQueryKeys.customFieldTypes())
          return produce(customFieldsDocument, (draftCustomFieldsDocument) => {
            if (!draftCustomFieldsDocument) {
              return draftCustomFieldsDocument
            }
            if (!draftCustomFieldsDocument.included) {
              draftCustomFieldsDocument.included = []
            }

            const fieldTypeInclude = draftCustomFieldsDocument.included.find(
              (i) => i.id === fieldTypeId
            )
            if (!fieldTypeInclude && fieldTypes) {
              draftCustomFieldsDocument.included.push(
                fieldTypes.data.find(
                  (f) => f.id === fieldTypeId
                ) as OpalApiComponent<'V3', 'custom_field_type'>
              )
            }

            draftCustomFieldsDocument.data.push(newCustomField)
            return draftCustomFieldsDocument
          })
        }
      )
    },
    onSuccess(_, { workspaceId }) {
      queryClient.invalidateQueries(globalQueryKeys.customFields(workspaceId))
    },
  })
}
